import * as circularJSON from 'circular-json';

import { ISO_DATE_REGEX, US_SLASH_DATE_REGEX } from '../constants/regex';
import { ISO_DATE_FORMAT, US_SLASH_DATE_FORMAT } from '../constants/util';
import { TokenValidationErrorsEnum } from '../enums/token-validation-errors.enum';

function isStringsEqual(firstValue: string, secondValue: string): boolean {
    if (typeof firstValue !== 'string' || typeof secondValue !== 'string') {
        return false;
    }

    return firstValue.trim().toLowerCase() === secondValue.trim().toLowerCase();
}

function isDatesEqual(firstDate: Date, secondDate: Date): boolean {
    if (!(firstDate instanceof Date) || !(secondDate instanceof Date)) {
        return false;
    }

    return (
        firstDate.getUTCMonth() === secondDate.getUTCMonth() &&
        firstDate.getUTCFullYear() == secondDate.getUTCFullYear() &&
        firstDate.getUTCDate() === secondDate.getUTCDate()
    );
}

function isISODateStringsEqual(firstDateString: string, secondDateString: string): boolean {
    if (typeof firstDateString !== 'string' || typeof secondDateString !== 'string') {
        return false;
    }

    const isoDateRegex = new RegExp(ISO_DATE_REGEX);

    if (!(isoDateRegex.test(firstDateString) && isoDateRegex.test(secondDateString))) {
        return false;
    }

    const firstDate = new Date(firstDateString);
    const secondDate = new Date(secondDateString);

    return isDatesEqual(firstDate, secondDate);
}

function formatFromCustomToISO(dateString: string): string {
    const customDateRegex = new RegExp(US_SLASH_DATE_REGEX);

    if (!(typeof dateString === 'string' && customDateRegex.test(dateString))) {
        return '';
    }

    const [month, day, year] = dateString.split('/');

    return `${year}-${month}-${day}`;
}

function formatFromISOToCustom(dateString: string): string {
    const customDateRegex = new RegExp(ISO_DATE_REGEX);

    if (!(typeof dateString === 'string' && customDateRegex.test(dateString))) {
        return '';
    }

    const [year, month, day] = dateString.split('-');

    return `${month}/${day}/${year}`;
}

function formatDateStringFactory(
    dateString: string,
    options: { from: string; to: string },
): string {
    /* istanbul ignore next */
    if (options?.from === US_SLASH_DATE_FORMAT && options?.to === ISO_DATE_FORMAT) {
        return formatFromCustomToISO(dateString);
    }

    if (options?.from === ISO_DATE_FORMAT && options?.to === US_SLASH_DATE_FORMAT) {
        return formatFromISOToCustom(dateString);
    }

    return '';
}

function stringifyClientPoId(obj: any, key: string): any {
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }

    const newObj = { ...obj };
    if (Object.prototype.hasOwnProperty.call(newObj, key)) {
        newObj[key] = newObj[key] + 'poid';
    }

    Object.keys(newObj).forEach((prop) => (newObj[prop] = stringifyClientPoId(newObj[prop], key)));

    return newObj;
}

function retrieveCookie(cookie) {
    return cookie.split(';').reduce((acc, item) => {
        const [key, value] = item.trim().split('=');
        acc[key] = value;
        return acc;
    }, {});
}

function stringifyCookie(cookie) {
    return Object.entries(cookie)
        .map(([key, value]) => {
            return `${key}=${value}`;
        })
        .join('; ');
}

function retrieveRequest(req, granteePoid) {
    const request = circularJSON.parse(circularJSON.stringify(req));
    const cookies = retrieveCookie(request.headers.cookie);

    delete cookies.SMSESSION;
    delete request.headers.sm_authdirname;
    delete request.headers['ext-user-profile-compressed'];
    delete request.headers['ext-user-profile'];
    cookies.POID = granteePoid;

    request.headers.cookie = stringifyCookie(cookies);
    return request;
}

function updateHeadersForGrantee(headers, headersForGrantee) {
    if (headersForGrantee['ext-user-profile-compressed']) {
        headers['ext-user-profile-compressed'] = headersForGrantee[
            'ext-user-profile-compressed'
        ] as string;
    }
    if (headersForGrantee['ext-user-profile']) {
        headers['ext-user-profile'] = headersForGrantee['ext-user-profile'] as string;
        headers['ext-user-profile-compressed'] = '';
    }
    headers.Authorization = headersForGrantee.authorization;
    return headers;
}

function isTokenValid(tokenValue: any): { valid: boolean; errors?: string } {
    const pattern: RegExp = /^[A-Za-z0-9]+$/;
    const errors: string[] = [];

    if (tokenValue === null || tokenValue === undefined || tokenValue === '') {
        errors.push(TokenValidationErrorsEnum.SHOULD_NOT_BE_EMPTY);
    }

    if (typeof tokenValue !== 'string') {
        errors.push(TokenValidationErrorsEnum.SHOULD_BE_A_STRING);
    }

    if (typeof tokenValue === 'string' && !pattern.test(tokenValue)) {
        errors.push(TokenValidationErrorsEnum.SHOULD_BE_ALPHANUMERIC);
    }

    return {
        valid: errors.length === 0,
        errors: errors.length ? errors.join(', ') : undefined,
    };
}

export default {
    isStringsEqual,
    isDatesEqual,
    isISODateStringsEqual,
    formatDateStringFactory,
    stringifyClientPoId,
    retrieveCookie,
    stringifyCookie,
    retrieveRequest,
    updateHeadersForGrantee,
    isTokenValid,
};
